import { CompassOutlined, DeleteOutlined, UnorderedListOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Checkbox, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useRef, useState } from 'react';
import { ScrollToBottomButton, ScrollToTopButton } from '../../../../components/ScrollButtunsTable';
import { useColumnDateTimeSearch } from '../../../../hooks/FilterTable/date';
import { useGetColumnSearchPropsTypeString } from '../../../../hooks/FilterTable/objString';
import { useTableScroll } from '../../hooks/useTableScroll';
import { IGerenciarMunicipiosItem } from '../../interface';
import { ModalDinamico } from '../ModalDinamico';
import { ModalRepublicarMunicipio } from '../ModalRepublicarMunicipio';

interface ITabelaGrupoProps {
    gerenciarHook: any;
    theme: string;
}

const { Text, Paragraph } = Typography;

export const TableGerenciarMunicipios = ({ gerenciarHook, theme }: ITabelaGrupoProps) => {
    const {
        loading,
        loadingDelete,
        response,
        getGerenciarMunicipios,
        deleteMunicipio,
    } = gerenciarHook;

    const {
        tableRef,
        scrollToTable
    } = useTableScroll();

    const [municipioParaDeletar, setMunicipioParaDeletar] = useState<IGerenciarMunicipiosItem | null>(null);
    const [modalDeletarVisible, setModalDeletarVisible] = useState(false);
    const [confirmedDelete, setConfirmedDelete] = useState(false);

    const [municipioParaRepublicar, setMunicipioParaRepublicar] = useState<IGerenciarMunicipiosItem | null>(null);
    const [modalRepublicarVisible, setModalRepublicarVisible] = useState(false);

    const refContagemDoFiltro = useRef<Array<any>>([]);

    useEffect(() => {
        getGerenciarMunicipios();
    }, [getGerenciarMunicipios]);

    const handleAbrirModalDeletar = (record: IGerenciarMunicipiosItem) => {
        setMunicipioParaDeletar(record);
        setConfirmedDelete(false);
        setModalDeletarVisible(true);
    };

    const handleFecharModalDeletar = () => {
        setConfirmedDelete(false);
        setModalDeletarVisible(false);
    };

    const handleConfirmarDelecao = async () => {
        if (deleteMunicipio && municipioParaDeletar) {
            await deleteMunicipio(municipioParaDeletar.id_ibge, municipioParaDeletar.sistema_slug);
            handleFecharModalDeletar();
        }
    };

    const handleAbrirModalRepublicar = (record: IGerenciarMunicipiosItem) => {
        setMunicipioParaRepublicar(record);
        setModalRepublicarVisible(true);
    };

    const municipiosList = response?.municipios || [];

    const sistemasOptions = Array.from(new Set(municipiosList
        .map((item: IGerenciarMunicipiosItem) => item.sistema)
        .filter(Boolean))).map(sistema => ({ text: sistema as string, value: sistema as string }));

    const columnsMunicipios: ColumnsType<IGerenciarMunicipiosItem> = [
        {
            title: "Município",
            dataIndex: "nome",
            key: "nome",
            ellipsis: true,
            ...useGetColumnSearchPropsTypeString('nome')
        },
        {
            title: "UF",
            dataIndex: "uf",
            key: "uf",
            width: 80,
            ...useGetColumnSearchPropsTypeString('uf')
        },
        {
            title: "IBGE",
            dataIndex: "id_ibge",
            key: "id_ibge",
            ellipsis: true,
            width: 160,
            ...useGetColumnSearchPropsTypeString('id_ibge')
        },
        {
            title: "Sistema",
            dataIndex: "sistema",
            key: "sistema",
            ellipsis: true,
            render: (sistema: string) => (
                <Tag color="blue">
                    {sistema}
                </Tag>
            ),
            filters: sistemasOptions,
            onFilter: (value, record) => record.sistema === value,
        },
        {
            title: "Conflito",
            dataIndex: "conflito",
            key: "conflito",
            width: 150,
            render: (conflito: boolean) => (
                <Tag color={conflito ? 'red' : 'green'}>
                    {conflito ? 'Sim' : 'Não'}
                </Tag>
            ),
            filters: [
                { text: 'Sim', value: true },
                { text: 'Não', value: false },
            ],
            onFilter: (value, record) => record.conflito === value,
        },
        {
            title: "Atualizado em",
            dataIndex: "atualizado_em",
            key: "atualizado_em",
            ...useColumnDateTimeSearch('atualizado_em'),
        },
        {
            title: "Ações",
            key: "acoes",
            width: 110,
            render: (record: IGerenciarMunicipiosItem) => {
                return (
                    <div className="divAcao" style={{ display: 'flex', gap: 6 }}>
                        <Tooltip title="Republicar Períodos" color="blue">
                            <Button
                                type="primary"
                                icon={<UnorderedListOutlined />}
                                onClick={() => handleAbrirModalRepublicar(record)}
                                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                            />
                        </Tooltip>
                        <Tooltip title="Apagar município" color="red">
                            <Button
                                type="primary"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleAbrirModalDeletar(record)}
                            />
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    const anos_a_excluir = municipioParaDeletar && Array.isArray(municipioParaDeletar.anos) && municipioParaDeletar.anos.length > 0
        ? municipioParaDeletar.anos.join(', ')
        : '';

    return (
        <>
            <div ref={tableRef}>
                <Table
                    rowKey={(record, index) => `${record.id_ibge}-${record.sistema_slug || index}`}
                    dataSource={municipiosList}
                    columns={columnsMunicipios}
                    loading={loading}
                    size="small"
                    pagination={{
                        locale: { items_per_page: '' },
                        showSizeChanger: true,
                        defaultPageSize: 50,
                        position: ['bottomRight'],
                        pageSizeOptions: ['10', '20', '50', '100']
                    }}
                    onChange={(_pagination, _filters, _sorter, extra) => {
                        refContagemDoFiltro.current = extra.currentDataSource;
                    }}
                    title={(registros) => {
                        return (
                            <div
                                style={{
                                    backgroundColor: theme === 'light' ? '#FAFAFA' : '#1D1D1D',
                                    padding: 10,
                                    marginBottom: -8,
                                    margin: -8,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <div>
                                    {response && (
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                            Total Geral: <b>{response.total_geral ?? 0}</b> | Conflitos: <b>{response.total_conflitos ?? 0}</b>
                                        </Text>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <ScrollToBottomButton
                                        registros={registros}
                                        onClick={() => scrollToTable('bottom')}
                                    />
                                    <Text>
                                        {`Mostrando: ${registros.length} de ${refContagemDoFiltro.current.length === 0 ?
                                            municipiosList.length :
                                            refContagemDoFiltro.current.length} em um total de ${municipiosList.length} `}
                                    </Text>
                                </div>
                            </div>
                        )
                    }}
                    footer={(registros) => {
                        return (
                            <div style={{ textAlign: 'end' }}>
                                <ScrollToTopButton
                                    registros={registros}
                                    onClick={() => scrollToTable('top')}
                                />
                                <Text>
                                    {`Mostrando: ${registros.length} de ${refContagemDoFiltro.current.length === 0 ?
                                        municipiosList.length :
                                        refContagemDoFiltro.current.length} em um total de ${municipiosList.length} `}
                                </Text>
                            </div>
                        )
                    }}
                />
            </div>

            <ModalDinamico
                visible={modalDeletarVisible}
                onClose={handleFecharModalDeletar}
                title="Apagar município do sistema"
                headerIcon={<WarningOutlined style={{ fontSize: 20, color: '#FFF' }} />}
                headerBgColor="#E0004D"
                confirmText="Apagar definitivamente"
                confirmDisabled={!confirmedDelete}
                confirmLoading={loadingDelete}
                onConfirm={handleConfirmarDelecao}
            >
                {municipioParaDeletar && (
                    <>
                        <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
                            Você está apagando <strong>{municipioParaDeletar.nome} ({municipioParaDeletar.uf})</strong> no sistema <strong>{municipioParaDeletar.sistema}</strong>.
                        </Paragraph>
                        <div
                            style={{
                                backgroundColor: theme === 'dark' ? '#2A171D' : '#FFF0F4',
                                border: `1px solid ${theme === 'dark' ? '#5C2A36' : '#FFD6E0'}`,
                                borderRadius: 6,
                                padding: 14,
                                marginBottom: 16,
                            }}
                        >
                            <Text style={{ color: '#C00038', fontWeight: 700, fontSize: 14, display: 'block', marginBottom: 6 }}>
                                Serão removidos TODOS os registros deste município neste sistema:
                            </Text>
                            <Text style={{ color: '#900028', fontSize: 13 }}>
                                {municipioParaDeletar.total_periodos ?? 0} registros
                                {anos_a_excluir ? ` · anos ${anos_a_excluir}` : ''} · todos os atributos de conexão (url, id_portal, cnpj, entidade)
                            </Text>
                        </div>
                        <Paragraph style={{ fontSize: 13, marginBottom: 16 }}>
                            A ação é definitiva e não pode ser desfeita por esta tela. Registros de outros sistemas e de outros municípios não são afetados.
                        </Paragraph>
                        <div
                            style={{
                                backgroundColor: theme === 'dark' ? '#888888c7' : '#F4F6F9',
                                border: `1px solid ${theme === 'dark' ? '#6d6b6bff' : '#E4E7EC'}`,
                                borderRadius: 6,
                                padding: 12,
                                marginBottom: 20,
                                display: 'flex',
                                gap: 10,
                                alignItems: 'flex-start',
                            }}
                        >
                            <CompassOutlined style={{ fontSize: 16, color: '#000', marginTop: 3 }} />
                            <Text style={{ color: '#000000ff', fontSize: 13 }}>
                                Em caso de erro ou exclusão indevida, <strong>entre em contato com o setor da programação</strong> imediatamente — somente eles podem avaliar a recuperação dos dados.
                            </Text>
                        </div>
                        <Checkbox
                            checked={confirmedDelete}
                            onChange={(e) => setConfirmedDelete(e.target.checked)}
                            style={{ fontSize: 13 }}
                        >
                            Entendi que todos os registros de <strong>{municipioParaDeletar.nome}</strong> no sistema <strong>{municipioParaDeletar.sistema}</strong> serão apagados.
                        </Checkbox>
                    </>
                )}
            </ModalDinamico>

            <ModalRepublicarMunicipio
                visible={modalRepublicarVisible}
                municipio={municipioParaRepublicar}
                onClose={() => setModalRepublicarVisible(false)}
                gerenciarHook={gerenciarHook}
            />
        </>
    );
};
